/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import type { Script, Meta } from '@unhead/vue'

export const SEO_URL = 'https://linenbylinas.com'
export const SEO_NAME = 'Linen By Linas'
export const SEO_DESCRIPTION_EN = 'Discover premium linen dresses, shirts, bedding, tablecloths and fabrics, dyed, sewn, and designed in Lithuania. Eco-friendly textiles with natural beauty.'
export const SEO_DESCRIPTION_LT = 'Atraskite linines sukneles, marškinius, patalynę, staltieses ir audinius tiesiai iš gamintojo Lietuvoje. Ekologiški gaminiai, derinantys grožį ir kokybę.'
export const SEO_RETURN_COUNTRIES = [
  'LT',
  'LV',
  'EE',
  'PL',
  'DE',
  'FR',
  'NL',
  'BE',
  'AT',
  'IT',
  'ES',
  'PT',
  'IE',
  'FI',
  'SE',
  'DK',
  'CZ',
  'SK',
  'SI',
  'HR',
  'HU',
  'RO',
  'BG',
  'GR',
  'LU',
  'MT',
  'CY',
  'GB',
  'US',
  'CA',
  'NO',
  'CH',
  'AU',
  'NZ',
  'JP',
]
export const seoOrgId = `${SEO_URL}/#organization`
const brandId = `${SEO_URL}/#brand`
export const seoProductId = (url: string) => `${url}#product`
const offerId = (url: string) => `${url}#offer`
export const seoBreadcrumbsId = (url: string) => `${url}#breadcrumbs`
export const seoWebsiteId = `${SEO_URL}/#website`

export function toFullUrlAdd(path: string): string {
  const base = SEO_URL
  if (!path || path === '/') return base

  const urlObj = import.meta.client && 'URL' in window ? new window.URL(base) : import.meta.server && 'URL' in global ? new global.URL(base) : null
  if (urlObj) {
    urlObj.pathname = path
    return urlObj.href
  }

  const sanitiedBaseUrl = base.charAt(base.length - 1) === '/' ? base.slice(0, base.length - 1) : base
  const sanitizePath = path.charAt(0) === '/' ? path.slice(1) : path
  return `${sanitiedBaseUrl}/${sanitizePath}`
}

export const SEO_LOGO = toFullUrlAdd('/lbl-logo-square.png')

type SchemaType = Record<string, unknown>

export function seoSchemaImage(img: None | Pick<gen.ImageNodeItemFragment, 'img' | 'url' | 'width' | 'height' | 'isVideo'>, caption: string) {
  if (!img || img.isVideo) return

  const url = img.img || img.url
  if (!url) return

  return {
    '@type': 'ImageObject',
    url,
    caption,
    ...(img.width && { width: img.width.toString() }),
    ...(img.height && { height: img.height.toString() }),
  }
}

export function seoSchemaVideo(img: None | Pick<gen.ImageNodeItemFragment, 'img' | 'url' | 'width' | 'height' | 'isVideo' | 'video' | 'uploadDate'>, opt: { name: string; description: string }) {
  if (!img?.isVideo || !img.img || !img.video || !img.uploadDate) return
  return { '@type': 'VideoObject', name: opt.name, description: opt.description, thumbnailUrl: img.img || img.url, contentUrl: img.video, uploadDate: img.uploadDate }
}

export function seoSchema(schema: SchemaType | (SchemaType | None)[]): Script | undefined {
  const innerHTML = helpers.tryCatch(() => {
    const arr = helpers.arrify(schema).filter(helpers.isDef)
    if (!arr.length) return undefined
    if (arr.length === 1) return { '@context': 'https://schema.org', ...arr[0] }
    return { '@context': 'https://schema.org', '@graph': arr }
  })
  return innerHTML ? { type: 'application/ld+json', innerHTML } : undefined
}

export function toValidPrice(value: number | string | None): number | undefined {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

export function toPriceValidUntil(endAt: Date | string | number | None): string | undefined {
  const end = helpers.toDate(endAt)
  if (!end || end.getTime() <= Date.now()) return undefined
  return end.toISOString().slice(0, 10)
}

export function schemaOffer({
  url,
  price,
  inStock,
  sku,
  priceOld,
  isMeters,
  endAt,
  returnsNotAllowed,
  availability,
  availabilityStarts,
  shippingDetails,
}: {
  url: string
  sku: string | undefined
  price: number | string
  priceOld: number | string | None
  inStock: boolean
  isMeters: boolean
  endAt: string | None
  returnsNotAllowed: boolean
  availability?: SeoAvailability
  availabilityStarts?: string | None
  shippingDetails?: SchemaType | None
}) {
  const activePrice = toValidPrice(price)
  const oldPrice = toValidPrice(priceOld)

  if (!activePrice) return

  const avail: SeoAvailability = availability || (inStock ? 'InStock' : 'OutOfStock')
  const priceValidUntil = toPriceValidUntil(endAt)

  const base = {
    '@type': 'Offer',
    '@id': offerId(url),
    url,
    ...(sku && { sku }),
    itemCondition: 'https://schema.org/NewCondition',
    availability: `https://schema.org/${avail}`,
    ...(avail === 'PreOrder' && availabilityStarts && { availabilityStarts }),
    hasMerchantReturnPolicy: returnsNotAllowed
      ? {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
          applicableCountry: SEO_RETURN_COUNTRIES,
        }
      : { '@id': `${SEO_URL}/info/returns#return-policy` },
    seller: { '@id': seoOrgId },
    ...(shippingDetails && { shippingDetails }),
    ...(priceValidUntil && { priceValidUntil }),
  }

  const meterRef = {
    '@type': 'QuantitativeValue',
    value: 1,
    unitCode: 'MTR',
  }

  if (isMeters) {
    const specs: Record<string, unknown>[] = [
      {
        '@type': 'UnitPriceSpecification',
        price: activePrice,
        priceCurrency: 'EUR',
        referenceQuantity: meterRef,
        valueAddedTaxIncluded: true,
      },
    ]

    if (oldPrice && oldPrice > activePrice) {
      specs.push({
        '@type': 'UnitPriceSpecification',
        priceType: 'https://schema.org/StrikethroughPrice',
        price: oldPrice,
        priceCurrency: 'EUR',
        referenceQuantity: meterRef,
        valueAddedTaxIncluded: true,
      })
    }

    return {
      ...base,
      priceSpecification: specs.length === 1 ? specs[0] : specs,
    }
  }

  const mainSpec = {
    '@type': 'UnitPriceSpecification',
    price: activePrice,
    priceCurrency: 'EUR',
    valueAddedTaxIncluded: true,
  }

  const strikeSpec =
    oldPrice && oldPrice > activePrice
      ? {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/StrikethroughPrice',
          price: oldPrice,
          priceCurrency: 'EUR',
          valueAddedTaxIncluded: true,
        }
      : undefined

  return {
    ...base,
    price: activePrice,
    priceCurrency: 'EUR',
    priceSpecification: strikeSpec ? [mainSpec, strikeSpec] : mainSpec,
  }
}

export function productAdditionalProperty(inp: { groupId: string | None }) {
  return inp?.groupId ? [{ '@type': 'PropertyValue', propertyID: 'item_group_id', value: inp?.groupId }] : []
}

export const schemaBrand = () => ({
  '@type': 'Brand',
  '@id': brandId,
  name: SEO_NAME,
})

export function schemaSearchAction() {
  return {
    '@type': 'SearchAction',
    target: `${SEO_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  }
}

export function seoAggregateRating(inp: { rating: number | None; count: number | None } | None) {
  if (!inp?.rating || !inp?.count || inp.count <= 0) return

  return {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: helpers.fit(helpers.round(inp.rating / 20, 1), 1, 5),
      reviewCount: inp.count,
    },
  }
}

export function seoMetaTitle(title: string | None): Meta[] {
  if (!title?.length) return []
  return [
    { property: 'og:title', content: title },
    { name: 'twitter:title', content: title },
  ]
}

export function seoShortText(value: string | None, max = 160): string {
  // TODO SEO - Rewrite to cut off on '.'
  const clean = (value || '').replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean

  const sliced = clean.slice(0, max - 1)
  return `${sliced.replace(/\s+\S*$/, '')}…`
}

export function seoMetaDescription(description: string | None): Meta[] {
  const content = seoShortText(description)
  if (!content?.length) return []
  return [
    { name: 'description', content },
    { property: 'og:description', content },
    { name: 'twitter:description', content },
  ]
}
const OG_IMAGE_WIDTH = 1200

export function toOgImage(url: string | None): string | undefined {
  if (!url) return undefined
  // don't proxy: already-proxied URLs and our own static assets (logo etc.)
  if (url.startsWith('https://img.linenbylinas.com/') || url.startsWith(SEO_URL)) return url
  return `https://img.linenbylinas.com/0/jpeg:w${OG_IMAGE_WIDTH}/plain/${url}`
}

export function seoMetaImage(
  full: string | None | Pick<gen.ImageNodeItemFragment, 'img' | 'url' | 'width' | 'height'>,
  type?: 'image/jpeg' | 'image/png' | None,
  card?: 'summary_large_image' | 'summary',
  alt?: string | None
): Meta[] {
  const obj = full && typeof full === 'object' && 'url' in full ? full : undefined
  const original: string | None = typeof full === 'string' ? full : obj?.img || obj?.url
  const image = toOgImage(original)
  if (!image?.length) return []

  const proxied = image !== original

  // dimensions must describe the URL we actually emit
  let width = obj?.width
  let height = obj?.height
  if (proxied && width && height && width > OG_IMAGE_WIDTH) {
    height = Math.round((height / width) * OG_IMAGE_WIDTH)
    width = OG_IMAGE_WIDTH
  }

  const tags: Meta[] = [
    { property: 'og:image', content: image },
    { property: 'og:image:secure_url', content: image },
    { property: 'og:image:type', content: proxied ? 'image/jpeg' : type || 'image/jpeg' },
    { name: 'twitter:card', content: card || 'summary_large_image' },
    { name: 'twitter:image', content: image },
    // { name: 'thumbnail', content: image },
  ]

  if (alt) {
    tags.push({ property: 'og:image:alt', content: alt }, { name: 'twitter:image:alt', content: alt })
  }

  if (width) tags.push({ property: 'og:image:width', content: width.toString() })
  if (height) tags.push({ property: 'og:image:height', content: height.toString() })

  return tags
}

export const seoWebPageId = (url: string) => `${url}#webpage`
export const seoPrimaryImageId = (url: string) => `${url}#primaryimage`

export function schemaWebPage({
  url,
  title,
  locale,
  image,
  hasBreadcrumb,
  dateModified,
}: {
  url: string
  title: string
  locale: string
  image: string | None | undefined
  dateModified: string | None
  hasBreadcrumb: boolean
}) {
  return {
    '@type': 'WebPage',
    '@id': seoWebPageId(url),
    url,
    name: title,
    inLanguage: locale,
    ...(image && { primaryImageOfPage: { '@type': 'ImageObject', '@id': seoPrimaryImageId(url), url: image } }),
    mainEntity: { '@id': seoProductId(url) },
    isPartOf: { '@id': seoWebsiteId },
    ...(hasBreadcrumb && { breadcrumb: { '@id': seoBreadcrumbsId(url) } }),
    ...(dateModified && { dateModified }),
  }
}

// --- Availability ---
export type SeoAvailability = 'InStock' | 'OutOfStock' | 'LimitedAvailability' | 'PreOrder' | 'BackOrder' | 'Discontinued'

export function toSeoAvailability(inp: { inStock: boolean; isDiscontinued?: boolean | None; upcoming?: boolean | None; lowStock?: boolean | None }): SeoAvailability {
  if (inp.isDiscontinued) return 'Discontinued'
  if (inp.upcoming) return 'PreOrder'
  if (!inp.inStock) return 'OutOfStock'
  if (inp.lowStock) return 'LimitedAvailability'
  return 'InStock'
}

export const seoReturnPolicyId = `${SEO_URL}/info/returns#return-policy`
export const seoShippingLtId = `${SEO_URL}/info/delivery#shipping-lt`

/** Minimal identity stub. Emitted on EVERY page (app.vue) so that
 *  Offer.seller and WebSite.publisher @id refs resolve. Merges with the
 *  full node on the homepage via shared @id. */
export function schemaOrgStub() {
  return {
    '@type': 'OnlineStore',
    '@id': seoOrgId,
    name: SEO_NAME,
    url: SEO_URL,
    logo: { '@type': 'ImageObject', url: SEO_LOGO, width: 316, height: 316 },
  }
}
